/**
 * RequirementsTraceMatrix — REQUIREMENT-SIDE traceability matrix.
 *
 * Distinct from the proposal-side Compliance Matrix. This traces every
 * requirement in the work-scope document (SOW / PWS / SOO / Section B/C),
 * organized by the SOURCE DOCUMENT's own header hierarchy (Level 1/2/3),
 * with full traceability: file name + page + section #.
 *
 * Lives as a section on the Ingestion page. The user picks the source file
 * (defaulted to the AI-suggested work-scope document); the AI then derives
 * the hierarchy and traceability.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ListTree, Loader2, Sparkles, Download, ChevronDown, ChevronUp,
  FileText, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import CollapsibleList from './CollapsibleList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RequirementsTraceMatrix({ pursuitId, authToken }) {
  const [isExpanded, setIsExpanded]   = useState(true);
  const [candidates, setCandidates]   = useState([]);
  const [suggestedId, setSuggestedId] = useState(null);
  // Multiple work-scope files (original + amendments), in selection order.
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [rows, setRows]               = useState([]);
  const [sourceFileName, setSourceFileName] = useState(null);
  const [building, setBuilding]       = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState(null);
  const [truncated, setTruncated]     = useState(false);

  const authHeaders = { Authorization: `Bearer ${authToken}` };

  // Load source candidates + any previously-built RTM
  useEffect(() => {
    if (!pursuitId || !authToken) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [candRes, rtmRes] = await Promise.all([
          fetch(`${API_URL}/api/pursuits/${pursuitId}/rtm-trace/source-candidates`, { headers: authHeaders }),
          fetch(`${API_URL}/api/pursuits/${pursuitId}/rtm-trace`, { headers: authHeaders }),
        ]);
        if (cancelled) return;
        if (candRes.ok) {
          const cand = await candRes.json();
          setCandidates(cand.candidates || []);
          setSuggestedId(cand.suggested_document_id || null);
          // Pre-check the AI suggestion (or first candidate); user adds amendments.
          if (cand.suggested_document_id) setSelectedDocIds([cand.suggested_document_id]);
          else if ((cand.candidates || []).length) setSelectedDocIds([cand.candidates[0].document_id]);
        } else {
          // Surface the failure instead of silently showing an empty picker.
          setLoadError(`Couldn't load documents (HTTP ${candRes.status}).`);
        }
        if (rtmRes.ok) {
          const rtm = await rtmRes.json();
          setRows(rtm.rows || []);
          setSourceFileName(rtm.source_file_name || null);
          // Restore the prior multi-file selection if a matrix was already built.
          const prior = rtm.source_document_ids || (rtm.source_document_id ? [rtm.source_document_id] : []);
          if (prior.length) setSelectedDocIds(prior);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Failed to load documents.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pursuitId, authToken]);

  const handleBuild = useCallback(async () => {
    if (!selectedDocIds.length) {
      toast.error('Select at least one work-scope document.');
      return;
    }
    // Build replaces the pursuit's canonical requirement set (what teaming
    // partners self-score against, and what the Extracted Requirements table /
    // Compliance Matrix use). Confirm since it overwrites prior requirements
    // and any partner scores on them.
    if (!window.confirm(
      'Build this Capability / Requirements Matrix from the selected SOW/PWS/SOO?\n\n' +
      'This becomes the pursuit\'s requirement set — replacing any existing extracted ' +
      'requirements and any partner self-scores already recorded against them.'
    )) {
      return;
    }
    setBuilding(true);
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/rtm-trace/build`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_ids: selectedDocIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.detail?.message || data?.detail || 'Build failed');
        return;
      }
      setRows(data.rows || []);
      setSourceFileName(data.source_file_name || null);
      setTruncated(!!data.truncated);
      toast.success(`Built ${data.row_count} requirement${data.row_count !== 1 ? 's' : ''} — now the pursuit's requirement set`, {
        description: data.truncated
          ? 'Note: document was long — only the first portion was processed. Partners will self-score these.'
          : 'Teaming partners self-score against these; also drives the Extracted Requirements table & Compliance Matrix.',
      });
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
    } finally {
      setBuilding(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pursuitId, authToken, selectedDocIds]);

  // Toggle a candidate doc in/out of the selection (preserves order added).
  const toggleDoc = (docId) => {
    setSelectedDocIds(prev =>
      prev.includes(docId) ? prev.filter(d => d !== docId) : [...prev, docId]
    );
  };

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/rtm-trace/docx`, { headers: authHeaders });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'RTM.docx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('RTM downloaded');
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pursuitId, authToken]);

  // Group rows by level_1 for banded display
  const grouped = rows.reduce((acc, r) => {
    const l1 = r.level_1 || '— Unassigned';
    if (!acc[l1]) acc[l1] = [];
    acc[l1].push(r);
    return acc;
  }, {});

  return (
    <Card className="border-slate-300 shadow-sm" data-testid="requirements-trace-matrix">
      <CardHeader
        className="cursor-pointer border-b border-slate-200 bg-slate-900 rounded-t-lg"
        onClick={() => setIsExpanded(v => !v)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ListTree className="w-5 h-5 text-amber-400" />
            <CardTitle className="text-white text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Capability / Requirements Matrix
            </CardTitle>
            {rows.length > 0 && (
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs">
                {rows.length} requirements
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-slate-400 hidden md:block">
              SOW / PWS / SOO · Level 1·2·3 · traceable to file · page · §
            </span>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-0">
          {/* Build bar */}
          <div className="flex items-end gap-3 px-5 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <label className="text-xs font-semibold text-slate-700 mb-1 block">
                Work-Scope Source File(s) — original + any amendments
              </label>
              <div className="bg-white border border-slate-300 rounded-md max-h-44 overflow-y-auto divide-y divide-slate-100" data-testid="rtm-source-list">
                {loading && (
                  <div className="px-3 py-2 text-xs text-slate-400">Loading documents…</div>
                )}
                {!loading && loadError && (
                  <div className="px-3 py-2 text-xs text-red-600 flex items-center gap-1.5">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />{loadError}
                  </div>
                )}
                {!loading && !loadError && candidates.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-400">
                    No documents uploaded for this pursuit yet. Upload the RFP/SOW under Manage Documents first.
                  </div>
                )}
                {candidates.map(c => {
                  const checked = selectedDocIds.includes(c.document_id);
                  return (
                    <label
                      key={c.document_id}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50"
                      data-testid={`rtm-source-opt-${c.document_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDoc(c.document_id)}
                        className="accent-amber-600"
                      />
                      <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      <span className="flex-1 truncate">{c.file_name}</span>
                      {c.group_name && (
                        <span className="text-[9px] text-slate-400 flex-shrink-0">{c.group_name}</span>
                      )}
                      {c.document_id === suggestedId && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[9px]">
                          AI suggested
                        </Badge>
                      )}
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                Check the SOW/PWS/SOO plus any amendments. Files are read in order — later picks are treated as amendments that supersede the original.
              </p>
            </div>
            <Button
              onClick={handleBuild}
              disabled={building || !selectedDocIds.length}
              className="bg-amber-600 hover:bg-amber-700 text-white h-9 text-xs gap-1.5"
              data-testid="rtm-build-btn"
            >
              {building ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {building ? 'Building…' : rows.length > 0 ? 'Rebuild Matrix' : 'Build Matrix'}
            </Button>
            {rows.length > 0 && (
              <Button
                variant="outline"
                onClick={handleDownload}
                disabled={downloading}
                className="h-9 text-xs gap-1.5 border-slate-300"
                data-testid="rtm-download-btn"
              >
                {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Word
              </Button>
            )}
          </div>

          {truncated && (
            <div className="flex items-center gap-2 px-5 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Document was long — only the first portion was traced. Consider splitting the SOW.
            </div>
          )}

          {/* Matrix body */}
          {rows.length === 0 ? (
            <div className="px-5 py-12 text-center text-slate-400">
              <ListTree className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium">No requirements traced yet</p>
              <p className="text-xs mt-1">
                Select your SOW/PWS/SOO above and click "Build Matrix" to extract and organize every requirement.
              </p>
            </div>
          ) : (
            <CollapsibleList count={rows.length} label="requirements" testId="rtm-trace-collapsible">
              {/* Column headers */}
              <div className="grid gap-2 items-center px-4 py-2 bg-slate-50 border-b border-slate-200 text-[9px] font-semibold uppercase tracking-wider text-slate-500"
                style={{ gridTemplateColumns: '1.4fr 3fr 0.5fr 1fr' }}>
                <div>Level 2 / Level 3</div>
                <div>Requirement</div>
                <div className="text-center">§ Ref</div>
                <div>Source (file · pg)</div>
              </div>

              {Object.entries(grouped).map(([l1, l1rows]) => (
                <div key={l1}>
                  {/* Level 1 band */}
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#2E5496]">
                    <span className="text-xs font-bold text-white uppercase tracking-wide">{l1}</span>
                    <span className="text-[10px] text-blue-200 ml-auto">{l1rows.length} req</span>
                  </div>
                  {l1rows.map((r, i) => (
                    <div
                      key={r.id}
                      className={cn(
                        'grid gap-2 items-start px-4 py-2.5 border-b border-slate-100 text-xs',
                        i % 2 ? 'bg-slate-50/50' : 'bg-white',
                      )}
                      style={{ gridTemplateColumns: '1.4fr 3fr 0.5fr 1fr' }}
                    >
                      {/* Level 2 / 3 */}
                      <div className="text-slate-600 leading-tight">
                        {r.level_2 && <span className="font-medium">{r.level_2}</span>}
                        {r.level_3 && (
                          <span className="block text-[11px] text-slate-400 mt-0.5 pl-2 border-l border-slate-200">
                            {r.level_3}
                          </span>
                        )}
                        {!r.level_2 && !r.level_3 && <span className="text-slate-300 italic">—</span>}
                      </div>
                      {/* Requirement */}
                      <div className="text-slate-800 leading-snug">{r.requirement_text}</div>
                      {/* Section ref */}
                      <div className="text-center">
                        <span className="inline-block font-mono text-[10px] text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">
                          {r.section_ref || '—'}
                        </span>
                      </div>
                      {/* Traceability */}
                      <div className="text-[10px] text-slate-500 leading-tight">
                        <span className="block truncate" title={r.file_name}>{r.file_name}</span>
                        {r.page && <span className="text-slate-400">p.{r.page}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </CollapsibleList>
          )}

          {rows.length > 0 && sourceFileName && (
            <div className="px-5 py-2 bg-slate-50 border-t border-slate-200 text-[10px] text-slate-400">
              Traced from: <span className="font-medium text-slate-600">{sourceFileName}</span>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
